import { ScoreResponse } from "../schema/score.schema";

import { PersonaView, SegmentView } from "./scoreConfigView";

/**
 * Pure edits on a working copy of the score (PREC-4055). Every settings page
 * edits the same draft through these reducers, and `toScoreBody` turns the
 * draft into what `PATCH /score` accepts. Rows created on the client carry a
 * negative temporary id until the API assigns a real one.
 */

type Category = ScoreResponse["prospectCompanyCategories"][number];
type Persona = ScoreResponse["prospectPersonaCategories"][number];

let nextTempId = -1;

/** A fresh negative id for a row that does not exist on the server yet. */
export const tempId = (): number => nextTempId--;

export const isTempId = (id: number | undefined): boolean => id !== undefined && id < 0;

const categoryLists = ["prospectCompanyCategories", "partnerCompanyCategories"] as const;

const mapCategories = (
	score: ScoreResponse,
	update: (category: Category) => Category
): ScoreResponse => ({
	...score,
	prospectCompanyCategories: score.prospectCompanyCategories.map(update),
	partnerCompanyCategories: score.partnerCompanyCategories.map(update),
});

const mapPersonas = (
	score: ScoreResponse,
	update: (persona: Persona) => Persona
): ScoreResponse => ({
	...score,
	prospectPersonaCategories: score.prospectPersonaCategories.map(update),
});

const findCategory = (score: ScoreResponse, id: number): Category | undefined =>
	categoryLists.flatMap((list) => score[list]).find((category) => category.id === id);

/* ---------- segments ---------- */

export interface SegmentPatch {
	category?: string;
	description?: string;
	active?: boolean;
}

export const updateSegment = (
	score: ScoreResponse,
	id: number,
	patch: SegmentPatch
): ScoreResponse =>
	mapCategories(score, (category) => (category.id === id ? { ...category, ...patch } : category));

export const addSegment = (
	score: ScoreResponse,
	group: "PROSPECT" | "PARTNER",
	seed: { category?: string; description?: string } = {},
	id: number = tempId()
): { score: ScoreResponse; id: number } => {
	const category: Category = {
		id,
		category: seed.category ?? "",
		description: seed.description ?? "",
		type: group,
		active: true,
	};
	const list = group === "PROSPECT" ? "prospectCompanyCategories" : "partnerCompanyCategories";

	return { score: { ...score, [list]: [...score[list], category] }, id };
};

/** Drops the segment from both lists and from every persona that was linked to it. */
export const removeSegment = (score: ScoreResponse, id: number): ScoreResponse => {
	const without = {
		...score,
		prospectCompanyCategories: score.prospectCompanyCategories.filter((c) => c.id !== id),
		partnerCompanyCategories: score.partnerCompanyCategories.filter((c) => c.id !== id),
	};

	return mapPersonas(without, (persona) => ({
		...persona,
		scoreCompanyCategories: (persona.scoreCompanyCategories ?? []).filter((c) => c.id !== id),
	}));
};

export const setPartners = (score: ScoreResponse, on: boolean): ScoreResponse => ({
	...score,
	partners: on,
});

export const setInvestors = (score: ScoreResponse, on: boolean): ScoreResponse => ({
	...score,
	investors: on,
});

/** Switches one investor type on or off; the last selected one cannot be switched off. */
export const setInvestorSelected = (
	score: ScoreResponse,
	investorName: string,
	selected: boolean
): ScoreResponse => {
	const investors = (score.investorConfiguration?.investors ?? []).map((investor) =>
		investor.name === investorName ? { ...investor, selected } : investor
	);

	if (!investors.some((investor) => investor.selected)) {
		return score;
	}

	return { ...score, investorConfiguration: { ...score.investorConfiguration, investors } };
};

/* ---------- personas ---------- */

export interface PersonaPatch {
	category?: string;
	description?: string;
	jobsToInclude?: string[];
	jobsToExclude?: string[];
	active?: boolean;
	investors?: boolean;
}

export const updatePersona = (
	score: ScoreResponse,
	id: number,
	patch: PersonaPatch
): ScoreResponse =>
	mapPersonas(score, (persona) => (persona.id === id ? { ...persona, ...patch } : persona));

export const addPersona = (
	score: ScoreResponse,
	seed: { category?: string; description?: string } = {},
	id: number = tempId()
): { score: ScoreResponse; id: number } => {
	const persona: Persona = {
		id,
		category: seed.category ?? "",
		description: seed.description ?? "",
		jobsToInclude: [],
		jobsToExclude: [],
		investors: false,
		active: true,
		scoreCompanyCategories: [],
	};

	return {
		score: { ...score, prospectPersonaCategories: [...score.prospectPersonaCategories, persona] },
		id,
	};
};

export const removePersona = (score: ScoreResponse, id: number): ScoreResponse => ({
	...score,
	prospectPersonaCategories: score.prospectPersonaCategories.filter((persona) => persona.id !== id),
});

/* ---------- links ---------- */

/**
 * Whether the persona exists in the segment. For an investor type this is the
 * persona's all-or-nothing `investors` flag — the API has no per-type link.
 */
export const setLink = (
	score: ScoreResponse,
	segment: SegmentView,
	personaId: number,
	on: boolean
): ScoreResponse =>
	mapPersonas(score, (persona) => {
		if (persona.id !== personaId) {
			return persona;
		}

		if (segment.investorName !== null) {
			return { ...persona, investors: on };
		}

		const linked = persona.scoreCompanyCategories ?? [];
		const already = linked.some((category) => category.id === segment.id);

		if (on && !already) {
			const category = segment.id === null ? undefined : findCategory(score, segment.id);

			return category ? { ...persona, scoreCompanyCategories: [...linked, category] } : persona;
		}

		if (!on && already) {
			return {
				...persona,
				scoreCompanyCategories: linked.filter((category) => category.id !== segment.id),
			};
		}

		return persona;
	});

export const rename = (
	score: ScoreResponse,
	kind: "segment" | "persona",
	row: SegmentView | PersonaView,
	name: string
): ScoreResponse =>
	kind === "segment"
		? updateSegment(score, (row as SegmentView).id ?? NaN, { category: name })
		: updatePersona(score, (row as PersonaView).id, { category: name });

/* ---------- save ---------- */

const withoutTempId = <TRow extends { id?: number }>(row: TRow): TRow =>
	isTempId(row.id) ? { ...row, id: undefined } : row;

/**
 * The draft as the API takes it: temporary ids removed so those rows are
 * created, and links to still-unsaved segments dropped (they cannot be linked
 * until they have an id; the page says so).
 */
export const toScoreBody = (draft: ScoreResponse): ScoreResponse => ({
	...draft,
	prospectCompanyCategories: draft.prospectCompanyCategories.map(withoutTempId),
	partnerCompanyCategories: draft.partnerCompanyCategories.map(withoutTempId),
	prospectPersonaCategories: draft.prospectPersonaCategories.map((persona) => ({
		...withoutTempId(persona),
		scoreCompanyCategories: (persona.scoreCompanyCategories ?? []).filter(
			(category) => !isTempId(category.id)
		),
	})),
});

/** Whether the draft differs from what the server holds. */
export const isDraftDirty = (draft: ScoreResponse, score: ScoreResponse): boolean =>
	JSON.stringify(draft) !== JSON.stringify(score);
